/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type { HostContext, LogLevel, Logger } from '../../@types';
import SearchHostContext from '../SearchHostContext';
import LoggerPayload from './LoggerPayload';
import LoggerService from './LoggerService';

/**
 * Prints the info, log, warn and error message in the Developer Console based on environment.
 */

class SearchLogger implements Logger {
	hostContext: HostContext;
	loggerPayload: LoggerPayload;
	loggerService: LoggerService;
	static instance: SearchLogger;

	constructor(hostContext: HostContext) {
		if (!SearchLogger.instance) {
			if (!hostContext) {
				throw new Error('SearchLogger requires a valid HostContext');
			}

			this.hostContext = hostContext;
			this.loggerPayload = new LoggerPayload(this.hostContext);
			this.loggerService = new LoggerService(this.hostContext);

			SearchLogger.instance = this;
		}

		return SearchLogger.instance;
	}

	// PUBLIC METHODS
	// Exceutes the Logger Info statement
	info(componentName: string, message: string, data?: any): void {
		this._logIt('info', componentName, message, data);
	}

	// Exceutes the Logger Log statement
	log(componentName: string, message: string, data?: any): void {
		this._logIt('log', componentName, message, data);
	}

	// Exceutes the Logger Warn statement
	warn(componentName: string, message: string, data?: any): void {
		this._logIt('warn', componentName, message, data);
	}

	// Exceutes the Logger Error statement
	error(componentName: string, message: string, data?: any): void {
		this._logIt('error', componentName, message, data);
	}

	//  PRIVATE METHODS

	/**
	 * Executes the logging action
	 *
	 * @param  {LogLevel} logLevel
	 * @param  {string} componentName
	 * @param  {string} message
	 * @param  {any} data
	 * @returns {null}
	 */
	_logIt(logLevel: LogLevel, componentName: string, message: string, data?: any): void {
		if (this.hostContext.featureFlags.includes('ccsearch_enable_ui_logger')) {
			if (message && componentName) {
				if (
					this.hostContext.environment !== 'prod' ||
					this.hostContext.featureFlags.includes('ccsearch_enable_ui_logger_console_prod')
				) {
					// Checking if the host has their own logger, if so we don't print our logs to the console; instead we print using their logger
					if (this.hostContext.logger && typeof this.hostContext.logger === 'function') {
						let msg = message;

						if (data?.server) {
							this.hostContext.logger(logLevel, `[CCSearch: ${componentName}] ${msg}`, data);
						} else {
							if (data) {
								msg += ` ${JSON.stringify(data)}`;
							}

							this.hostContext.logger(logLevel, `[CCSearch: ${componentName}] ${msg}`);
						}
					} else {
						this.consoleLog(logLevel, componentName, message, data);
					}
				}

				this.shouldBeLoggedOnServer(logLevel, data) &&
					this.loggerService.sendLog(logLevel, this.loggerPayload.getPayload(componentName, message, data));
			} else {
				console.error(
					`[Logger Syntax Error] Component Name or Message missing in logger.${logLevel} statement`,
				);
				console.error(`logger.${logLevel} syntax:-`);
				console.error(`logger.${logLevel}('Component Name', 'Message'`);
				console.error(`logger.${logLevel}('Component Name', 'Message', 'Data')`);
			}
		}
	}

	/**
	 * Prints the appropriate console statement for the respective logLevel
	 *
	 * @param {LogLevel} logLevel
	 * @param {string} componentName
	 * @param {string} message
	 * @param {*} data
	 */
	consoleLog(logLevel: LogLevel, componentName: string, message: string, data?: any): void {
		switch (logLevel) {
			case 'info':
				console.info(`[CCSearch: ${componentName}]`, message, data || '');
				break;

			case 'log':
				console.log(`[CCSearch: ${componentName}]`, message, data || '');
				break;

			case 'warn':
				console.warn(`[CCSearch: ${componentName}]`, message, data || '');
				break;

			case 'error':
				console.error(`[CCSearch: ${componentName}]`, message, data || '');
				break;

			default:
				console.log(`[CCSearch: ${componentName}]`, message, data || '');
		}
	}

	/**
	 * Checks whether the particular log needs to be sent on server
	 * @param logLevel {string}
	 * @param data {Object}
	 * @returns {boolean} true if the server logging required
	 */
	shouldBeLoggedOnServer(logLevel: LogLevel, data?: any): boolean {
		const hostHasSplunkLoggingEnabled = this.hostContext.featureFlags.includes('ccsearch_enable_ui_logger_splunk');
		const hostEnvironmentIsStageOrProd =
			this.hostContext.environment === 'stage' || this.hostContext.environment === 'prod';
		const hasServerInfo = data?.server;
		const logLevelIsNotInfo = logLevel !== 'info';

		return (hasServerInfo || logLevelIsNotInfo) && hostHasSplunkLoggingEnabled && hostEnvironmentIsStageOrProd;
	}
}

const getLogger = (): SearchLogger => new SearchLogger(SearchHostContext.getContext());

export { SearchLogger, getLogger };
